import React, { useState, useRef, useId } from 'react';
import { Upload, X, Loader, ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 5;

export default function ImageUpload({ images = [], onChange, folder = 'rentalx/properties' }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputId = useId();

  const uploadFile = async (file) => {
    if (!file.type.startsWith('image/')) { setError('Only image files allowed.'); return null; }
    if (file.size > 8 * 1024 * 1024) { setError('Max file size is 8MB.'); return null; }

    try {
      // 1. Get signed params from backend
      const sig = await fetch(`${API}/api/cloudinary/signature?folder=${folder}`).then(r => r.json());

      // 2. Upload directly to Cloudinary
      const form = new FormData();
      form.append('file', file);
      form.append('api_key', sig.api_key);
      form.append('timestamp', sig.timestamp);
      form.append('signature', sig.signature);
      form.append('folder', sig.folder);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: form }
      );
      const data = await res.json();
      if (data.secure_url) return data.secure_url;
      setError('Upload failed. Please try again.');
      return null;
    } catch {
      setError('Upload failed. Check your connection.');
      return null;
    }
  };

  const handleFiles = async (files) => {
    if (images.length >= MAX_IMAGES) { setError(`Maximum ${MAX_IMAGES} images allowed.`); return; }
    setError('');
    setUploading(true);

    const toUpload = Array.from(files).slice(0, MAX_IMAGES - images.length);
    const urls = [];

    for (const file of toUpload) {
      const url = await uploadFile(file);
      if (url) urls.push(url);
    }

    if (urls.length) onChange([...images, ...urls]);
    setUploading(false);
  };

  const removeImage = (idx) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3">
      {/* Preview grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {images.map((url, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-sky-100 group">
              <img src={url} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
              {i === 0 && (
                <div className="absolute bottom-0 left-0 right-0 bg-[#094f6f] text-white text-[10px] text-center py-0.5 font-medium">
                  Cover
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      {images.length < MAX_IMAGES && (
        <div
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          className="border-2 border-dashed border-sky-200 rounded-xl p-6 text-center hover:border-[#094f6f] transition-colors cursor-pointer"
          onClick={() => document.getElementById(inputId).click()}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-[#094f6f]">
              <Loader className="w-5 h-5 animate-spin" />
              <span className="text-sm font-medium">Uploading to Cloudinary...</span>
            </div>
          ) : (
            <>
              <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600">Drop images here or <span className="text-[#094f6f]">browse</span></p>
              <p className="text-xs text-gray-400 mt-1">
                JPG, PNG, WebP — max 8MB per image · {images.length}/{MAX_IMAGES} uploaded
              </p>
            </>
          )}
        </div>
      )}

      <input
        id={inputId}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
        data-testid="image-upload-input"
      />

      {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
  );
}
